import { CardView } from "@/components/v2/CardView";
import { useTrans } from "@/translations";

import { useMemo, useState } from "react";

import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import type { BarShapeProps } from "recharts";

import { ViewWithLoader } from "../ViewWithLoader";
import { BarShape } from "./components/BarShape";
import { CategoryTick } from "./components/CategoryTick";
import {
  ASPECT_RATIO,
  CHART_MARGIN_SIDE,
  CHART_MARGIN_TOP,
  EMPTY_STATE_HEIGHT,
} from "./constants";
import type { BarChartDatum } from "./types";
import { getBottomMargin } from "./utils";

type Props = {
  title: string;
  subtitle?: string;
  isLoading: boolean;
  data: BarChartDatum[];
  valueFormatter?: (value: number) => string;
};

export const BarChartView: React.FC<Props> = ({
  title,
  subtitle,
  isLoading,
  data,
  valueFormatter = (value) => `${value}`,
}) => {
  const { t } = useTrans();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const yMax = useMemo(
    () => Math.max(...data.map((d) => d.value ?? 0), 1),
    [data],
  );

  const marginBottom = useMemo(() => getBottomMargin(data), [data]);

  if (!data.length && !isLoading) {
    return (
      <CardView title={title} subtitle={subtitle}>
        <div
          className="text-grey6 flex flex-1 items-center justify-center text-[16px]"
          style={{ minHeight: EMPTY_STATE_HEIGHT }}
        >
          {t("portfolioPage:noBuildingsText")}
        </div>
      </CardView>
    );
  }

  return (
    <CardView title={title} subtitle={subtitle}>
      <ViewWithLoader isLoading={isLoading}>
        <div className="mt-4 flex-1">
          <ResponsiveContainer width="100%" aspect={ASPECT_RATIO}>
            <BarChart
              data={data}
              margin={{
                top: CHART_MARGIN_TOP,
                right: CHART_MARGIN_SIDE,
                bottom: marginBottom,
                left: CHART_MARGIN_SIDE,
              }}
              barCategoryGap={0}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                interval={0}
                height={marginBottom}
                tick={(props: any) => (
                  <CategoryTick
                    x={props.x}
                    y={props.y}
                    payload={props.payload}
                  />
                )}
              />
              <YAxis hide domain={[0, yMax]} />
              <Bar
                dataKey="value"
                shape={(props: BarShapeProps) => (
                  <BarShape
                    {...props}
                    hoveredIndex={hoveredIndex}
                    valueFormatter={valueFormatter}
                  />
                )}
                isAnimationActive={false}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ViewWithLoader>
    </CardView>
  );
};
